import type {
    DiscountType,
    MessageLog,
    PaymentMethod,
    StaffDuty
} from '@/types'
import type {
    Command,
    RegisterJobInput,
    ServiceInput,
    SettingsInput
} from '@/lib/carwash/engine'

import { useMemo } from 'react'
import { useToast } from '@/components/kit/toast'
import { useCarWash } from '@/hooks/use-carwash'
import { createId } from '@/lib/carwash/ids'

export type RegisterVehicleInput = Omit<RegisterJobInput, 'jobId' | 'vehicleId' | 'customerId'>

export type StaffInput = {
    name: string
    phone: string | null
    photo: string | null
}

export type VehicleInput = {
    model: string | null
    ownerName: string | null
    ownerPhone: string | null
}

/*
 * The operations screens use. Each one builds a command with fresh ids,
 * runs it through the business rules and shows the reason when it is
 * refused, so a screen only has to say what succeeded.
 */
export const useCarWashActions = () => {
    const { run } = useCarWash()
    const toast = useToast()

    return useMemo(() => {
        const exec = (command: Command): boolean => {
            const result = run(command)
            if (!result.ok) toast({ tone: 'error', title: result.error })
            return result.ok
        }

        return {
            registerVehicle: (input: RegisterVehicleInput): string | null => {
                const jobId = createId()
                const ok = exec({ type: 'job/register', input: { ...input, jobId, vehicleId: createId(), customerId: createId() } })
                return ok ? jobId : null
            },
            assignStaff: (jobId: string, staffId: string, options: { start: boolean, allowBusyStaff?: boolean }): boolean => exec({
                type: 'job/assign',
                jobId,
                staffId,
                start: options.start,
                allowBusyStaff: options.allowBusyStaff ?? false
            }),
            unassignStaff: (jobId: string): boolean => exec({ type: 'job/unassign', jobId }),
            startWash: (jobId: string): boolean => exec({ type: 'job/start', jobId }),
            sendToQualityCheck: (jobId: string): boolean => exec({ type: 'job/quality-check', jobId }),
            sendBackToWashing: (jobId: string): boolean => exec({ type: 'job/rework', jobId }),
            completeWash: (jobId: string): string | null => {
                const invoiceId = createId()
                return exec({ type: 'job/complete', jobId, invoiceId }) ? invoiceId : null
            },
            cancelJob: (jobId: string): boolean => exec({ type: 'job/cancel', jobId }),
            setJobPhoto: (jobId: string, photo: string | null): boolean => exec({ type: 'job/photo', jobId, photo }),
            updateJobVehicle: (
                jobId: string,
                input: { plate: string, vehicleModel: string | null, owner?: { name: string | null, phone: string | null } }
            ): boolean => exec({
                type: 'job/vehicle',
                jobId,
                plate: input.plate,
                vehicleModel: input.vehicleModel,
                vehicleId: createId(),
                owner: input.owner ? { ...input.owner, customerId: createId() } : undefined
            }),
            applyDiscount: (invoiceId: string, discountType: DiscountType, discountValue: number): boolean => exec({
                type: 'invoice/discount',
                invoiceId,
                discountType,
                discountValue
            }),
            markPaid: (invoiceId: string, method: PaymentMethod): boolean => exec({ type: 'invoice/pay', invoiceId, method, paymentId: createId() }),
            markPending: (invoiceId: string): boolean => exec({ type: 'invoice/unpay', invoiceId }),
            createStaff: (input: StaffInput): string | null => {
                const staffId = createId()
                return exec({ type: 'staff/create', staffId, ...input }) ? staffId : null
            },
            updateStaff: (staffId: string, input: StaffInput): boolean => exec({ type: 'staff/update', staffId, ...input }),
            setStaffDuty: (staffId: string, duty: StaffDuty): boolean => exec({ type: 'staff/duty', staffId, duty }),
            createService: (service: ServiceInput): string | null => {
                const serviceId = createId()
                return exec({ type: 'service/create', serviceId, service }) ? serviceId : null
            },
            updateService: (serviceId: string, service: ServiceInput): boolean => exec({ type: 'service/update', serviceId, service }),
            setServiceActive: (serviceId: string, active: boolean): boolean => exec({ type: 'service/active', serviceId, active }),
            updateVehicle: (vehicleId: string, input: VehicleInput): boolean => exec({ type: 'vehicle/update', vehicleId, ...input, customerId: createId() }),
            updateSettings: (settings: SettingsInput): boolean => exec({ type: 'settings/update', settings }),
            logMessage: (message: Omit<MessageLog, 'id' | 'openedAt' | 'channel'>): boolean => exec({
                type: 'message/log',
                message: {
                    ...message,
                    id: createId(),
                    channel: 'whatsapp_link',
                    openedAt: new Date().toISOString()
                }
            })
        }
    }, [run, toast])
}

export type CarWashActions = ReturnType<typeof useCarWashActions>
